import React from 'react';
import './Table.css';

/**
 * Universal Table Component
 * @param {Array} columns - Column definitions: { header, accessor, render, width, align }
 * @param {Array} data - Array of data objects to display
 * @param {boolean} isLoading - Shows loading state
 * @param {string|node} emptyMessage - Message when data is empty
 * @param {string|node} emptyIcon - Icon class for empty state (e.g. "fas fa-inbox")
 * @param {function} onRowClick - Optional row click handler (item) => void
 * @param {string} className - Additional CSS class for the table container
 */
export default function Table({
  columns = [],
  data = [],
  isLoading = false,
  emptyMessage = 'No data found.',
  emptyIcon = 'fas fa-inbox',
  onRowClick,
  className = '',
}) {
  const colCount = columns.length;

  return (
    <div className={`ui-table-container ${className}`}>
      <table className="ui-table">
        <colgroup>
          {columns.map((col, i) => (
            <col key={i} style={col.width ? { width: col.width } : undefined} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={i}
                style={{ textAlign: col.align || 'left' }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {/* Loading State */}
          {isLoading && (
            <tr className="ui-table-state-row">
              <td colSpan={colCount}>
                <div className="ui-table-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading...</p>
                </div>
              </td>
            </tr>
          )}

          {/* Empty State */}
          {!isLoading && data.length === 0 && (
            <tr className="ui-table-state-row">
              <td colSpan={colCount}>
                <div className="ui-table-state empty">
                  {emptyIcon && <i className={emptyIcon}></i>}
                  <p>{emptyMessage}</p>
                </div>
              </td>
            </tr>
          )}

          {/* Data Rows */}
          {!isLoading && data.map((item, rowIdx) => (
            <tr
              key={item.id || item.key || rowIdx}
              className={onRowClick ? 'ui-table-row clickable' : 'ui-table-row'}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col, colIdx) => (
                <td
                  key={colIdx}
                  style={{ textAlign: col.align || 'left' }}
                >
                  {col.render
                    ? col.render(item)
                    : item[col.accessor] ?? '—'
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
